import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { MODULES, AI_MODULE } from "@/lib/modules";

export default function Product() {
  return (
    <div data-testid="product-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white border-b border-slate-200">
        <div className="absolute inset-0 bg-dots opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#22A34A] mb-4">
              The Product
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
              Every module you need — connected and AI-supported.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              QC4Health is the only fully integrated clinical governance
              platform on the market. Seven modules, one shared risk engine,
              embedded AI throughout.
            </p>
            <div className="mt-9">
              <Link
                to="/book-demo"
                data-testid="product-hero-book-demo-btn"
                className="inline-flex items-center gap-2 bg-[#22A34A] hover:bg-[#1e9042] text-white rounded-full px-8 py-3.5 font-medium transition-all hover:-translate-y-0.5 shadow-[0_10px_28px_-8px_rgba(34,163,74,0.55)]"
              >
                Book a Demo
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES DETAILED */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="space-y-16 md:space-y-24">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              const reversed = i % 2 === 1;
              return (
                <div
                  key={m.slug}
                  id={m.slug}
                  className={`grid lg:grid-cols-12 gap-10 lg:gap-14 items-center ${
                    reversed ? "" : ""
                  }`}
                  data-testid={`product-module-${m.slug}`}
                >
                  <div
                    className={`lg:col-span-6 ${
                      reversed ? "lg:order-2" : ""
                    }`}
                  >
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#22A34A]/10 text-[#22A34A] text-[11px] font-bold tracking-[0.18em] uppercase mb-5">
                      Module 0{i + 1}
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
                      {m.title}
                    </h2>
                    <p className="mt-3 text-lg text-[#1F5D8C] font-medium">
                      {m.tagline}
                    </p>
                    <p className="mt-5 text-slate-600 leading-relaxed">
                      {m.description}
                    </p>
                    <ul className="mt-8 space-y-3">
                      {m.bullets.map((b, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <span className="mt-1 w-5 h-5 rounded-full bg-[#22A34A]/15 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-[#22A34A]" />
                          </span>
                          <span className="text-slate-700">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className={`lg:col-span-6 ${reversed ? "lg:order-1" : ""}`}>
                    <div className="aspect-[4/3] rounded-3xl bg-gradient-to-br from-white to-slate-50 border border-slate-200 shadow-sm p-8 flex items-center justify-center relative overflow-hidden">
                      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#22A34A]/8 blur-3xl" />
                      <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-[#3FB8E5]/10 blur-3xl" />
                      <div className="relative flex flex-col items-center text-center">
                        <div className="w-24 h-24 rounded-3xl bg-white shadow-lg border border-slate-100 flex items-center justify-center mb-6">
                          <Icon className="w-10 h-10 text-[#22A34A]" />
                        </div>
                        <div className="font-heading text-xl font-semibold text-slate-900 max-w-xs">
                          {m.title}
                        </div>
                        <div className="mt-1 text-sm text-slate-500 max-w-xs">
                          {m.tagline}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* AI section */}
            <div
              className="rounded-3xl overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#0F172A] to-[#1F5D8C] p-10 md:p-14 text-white relative"
              data-testid="product-module-ai"
            >
              <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#3FB8E5]/25 blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-[#22A34A]/15 blur-3xl" />
              <div className="relative grid lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-6">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center mb-6">
                    <Sparkles className="w-6 h-6 text-[#3FB8E5]" />
                  </div>
                  <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#3FB8E5] mb-3">
                    Embedded Throughout
                  </div>
                  <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
                    {AI_MODULE.title}
                  </h2>
                  <p className="mt-3 text-lg text-white/80">{AI_MODULE.tagline}</p>
                  <p className="mt-5 text-white/70 leading-relaxed">
                    {AI_MODULE.description}
                  </p>
                </div>
                <div className="lg:col-span-6 grid sm:grid-cols-2 gap-4">
                  {[
                    "AI-assisted policies & procedures",
                    "Insight from incidents & trends",
                    "Predictive assurance",
                    "Continuous improvement intelligence",
                    "Accreditation-ready evidence packs",
                  ].map((b, i) => (
                    <div
                      key={i}
                      className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5"
                      data-testid={`ai-feature-${i}`}
                    >
                      <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#3FB8E5] mb-2">
                        Feature 0{i + 1}
                      </div>
                      <div className="font-heading font-medium">{b}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">
            Ready to see it in your hospital?
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            60 minutes with our team. Tailored to your governance program.
          </p>
          <Link
            to="/book-demo"
            data-testid="product-footer-book-demo-btn"
            className="mt-8 inline-flex items-center gap-2 bg-[#22A34A] hover:bg-[#1e9042] text-white rounded-full px-8 py-3.5 font-medium transition-all hover:-translate-y-0.5 shadow-[0_10px_28px_-8px_rgba(34,163,74,0.55)]"
          >
            Book a Demo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
